import React, { useCallback, useEffect, useState } from 'react'
import { motion } from 'motion/react'
import { getPushStatus, sendTestPush, type PushTokenSummary, type TestPushResult } from '../../api/client'
import { isNativeShell, pushPermission, type PushPermission } from '../../lib/nativeShell'
import { enablePushNotifications } from '../../lib/pushRegistration'

/**
 * The developer's push controls: prompt for permission, and send this
 * account's devices a test push.
 *
 * Shown only inside the app shell, and only where the backend has
 * `DEV_TOOLS_ENABLED` (the status call reports it). When Honeyguide should
 * ask a user for push is a product decision not yet made, so nothing here is
 * for users; this is how the path is proved end to end.
 */

const PERMISSION_LABEL: Record<PushPermission, string> = {
    granted: 'Notifications are allowed on this device.',
    denied: 'Notifications are turned off for Honeyguide in iOS Settings.',
    prompt: 'This device has not been asked yet.',
    unavailable: 'This app build has no push support.',
}

const buttonSpring = { type: 'spring', stiffness: 400, damping: 20 } as const

const PushSettings: React.FC = () => {
    const [tokens, setTokens] = useState<PushTokenSummary[]>([])
    const [testAvailable, setTestAvailable] = useState(false)
    const [permission, setPermission] = useState<PushPermission>('unavailable')
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)
    const [message, setMessage] = useState<string | null>(null)
    const [results, setResults] = useState<TestPushResult[] | null>(null)

    const refresh = useCallback(async () => {
        try {
            const [status, perm] = await Promise.all([getPushStatus(), pushPermission()])
            setTokens(status.tokens)
            setTestAvailable(status.test_push_available)
            setPermission(perm)
        } catch {
            // A failed status read leaves the section hidden.
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (!isNativeShell()) {
            setLoading(false)
            return
        }
        void refresh()
    }, [refresh])

    const handleEnable = useCallback(async () => {
        setBusy(true)
        setMessage(null)
        setResults(null)
        try {
            const outcome = await enablePushNotifications()
            setPermission(outcome.permission)
            setMessage(
                outcome.registered
                    ? 'Registered this device.'
                    : outcome.error ?? PERMISSION_LABEL[outcome.permission],
            )
        } finally {
            setBusy(false)
            void refresh()
        }
    }, [refresh])

    const handleTest = useCallback(async () => {
        setBusy(true)
        setMessage(null)
        setResults(null)
        try {
            const sent = await sendTestPush()
            setResults(sent)
            if (sent.length === 0) setMessage('No device is registered for this account.')
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
            setMessage(detail ?? 'The test push could not be sent.')
        } finally {
            setBusy(false)
            void refresh()
        }
    }, [refresh])

    if (loading || !isNativeShell() || !testAvailable) return null

    return (
        <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold mb-1">Push notifications</h2>
            <p className="text-sm text-gray-600 mb-4">
                Developer controls. {PERMISSION_LABEL[permission]}
            </p>

            <div className="flex flex-wrap gap-3 mb-4">
                <motion.button
                    type="button"
                    onClick={handleEnable}
                    disabled={busy || permission === 'unavailable' || permission === 'denied'}
                    className="py-2 px-4 bg-action text-white rounded-md disabled:opacity-50"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    transition={buttonSpring}
                >
                    {permission === 'granted' ? 'Re-register this device' : 'Enable notifications'}
                </motion.button>
                <motion.button
                    type="button"
                    onClick={handleTest}
                    disabled={busy || tokens.length === 0}
                    className="py-2 px-4 border border-gray-300 rounded-md disabled:opacity-50"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    transition={buttonSpring}
                >
                    Send me a test push
                </motion.button>
            </div>

            {message && <p className="text-sm text-gray-700 mb-3">{message}</p>}

            {results && results.length > 0 && (
                <ul className="text-sm mb-3 flex flex-col gap-1">
                    {results.map(r => (
                        <li key={r.token_suffix} className={r.ok ? 'text-green-700' : 'text-rose-600'}>
                            …{r.token_suffix} ({r.app_id}):{' '}
                            {r.ok ? 'sent' : `${r.reason ?? 'failed'} (${r.status})`}
                            {r.removed ? ', token removed' : ''}
                        </li>
                    ))}
                </ul>
            )}

            <div className="text-sm text-gray-600">
                {tokens.length === 0 ? (
                    <span>No device registered.</span>
                ) : (
                    <ul className="flex flex-col gap-1">
                        {tokens.map(t => (
                            <li key={t.token_suffix}>
                                {t.platform} …{t.token_suffix} ({t.app_id})
                                {t.updated_at && (
                                    <span className="text-gray-500">
                                        {' '}
                                        updated{' '}
                                        {new Date(t.updated_at).toLocaleString(undefined, {
                                            dateStyle: 'medium',
                                            timeStyle: 'short',
                                        })}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    )
}

export default PushSettings
